/**
 * Domain sales lander — marketplace redesign, Feb 2026.
 *
 * A single template (marketplace) replaces the old editorial / minimal /
 * poster alt-templates. The `template` field on a listing is now ignored
 * — every listing gets the same navy-hero + floating purchase-card layout.
 *
 * All checkout/analytics/offer business logic is preserved from the
 * previous implementation; only the JSX presentation is new.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck, Clock, ArrowRight, ArrowUpRight, Zap, X, Check, Globe,
  CreditCard, Handshake, Repeat, KeyRound,
} from "lucide-react";
import { init as swetrixInit, track as swetrixApiTrack, trackViews as swetrixTrackViews } from "swetrix";
import {
  fetchListing, submitOffer, trackEvent, fetchPaymentStatus,
  fetchActivity, fetchCheckoutPlan, createBunqPayment, fetchBunqStatus,
  fetchEmbeddedStatus, fetchIntentStatus, fetchComps, fetchLinkedProjects,
  validateDiscount, api,
} from "@/lib/api";
import { useBranding } from "@/lib/branding";
import SiteHeader from "@/components/marketplace/SiteHeader";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/marketplace/SiteFooter";
import CompareTray from "@/components/marketplace/CompareTray";
import QRHandoff from "@/components/marketplace/QRHandoff";
import SnitcherEmbed from "@/components/marketplace/SnitcherEmbed";
import ShareRow from "@/components/marketplace/ShareRow";
import PaymentMethods from "@/components/marketplace/PaymentMethods";
import BullseyeEmbed from "@/components/marketplace/BullseyeEmbed";
import { gradientFromDomain as _gfd } from "@/lib/gradient";
import StripeEmbeddedModal from "@/components/StripeEmbeddedModal";
import StripePaymentElement from "@/components/StripePaymentElement";

let __swetrixInit = false;
function ensureSwetrix(projectId) {
  if (__swetrixInit || !projectId) return;
  try {
    swetrixInit(projectId, { apiURL: "https://api.swetrix.com/log", devMode: false });
    swetrixTrackViews({ heartbeatOnBackground: true });
    __swetrixInit = true;
  } catch { /* silent */ }
}
function swetrixTrack(event, meta) {
  if (!__swetrixInit) return;
  try { swetrixApiTrack({ ev: event, meta: meta || {} }); } catch { /* silent */ }
}

function currencySymbol(c) {
  return ({ EUR: "€", USD: "$", GBP: "£" })[(c || "EUR").toUpperCase()] || `${c || "EUR"} `;
}
function getSessionId() {
  const k = "sniper_sid";
  let v = localStorage.getItem(k);
  if (!v) {
    v = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(k, v);
  }
  return v;
}

export default function Lander() {
  const { slug } = useParams();
  const brand = useBranding();
  const [listing, setListing] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", amount: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [buying, setBuying] = useState(false);
  const [payStatus, setPayStatus] = useState(null);
  const [activity, setActivity] = useState(null);
  const [comps, setComps] = useState([]);
  const [linkedProjects, setLinkedProjects] = useState([]);
  const [publishableKey, setPublishableKey] = useState("");
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [showStripe, setShowStripe] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  // Share-nudge discount applied to this checkout (server-validated).
  const [discount, setDiscount] = useState(null);
  const [selectedOption, setSelectedOption] = useState("buy");
  // True when the current URL indicates the buyer just came back from a
  // Stripe wallet redirect (iDEAL/Bancontact/EPS) or a paid Card path.
  // Set once on mount from URLSearchParams so the confirmation screen
  // shows for the entire duration of the polling loop.
  const [returnFlow, setReturnFlow] = useState(() => {
    if (typeof window === "undefined") return false;
    const p = new URLSearchParams(window.location.search);
    return Boolean(p.get("payment_intent") || p.get("pi") || p.get("session_id"));
  });
  const scrollTracked = useRef(new Set());
  const startedAt = useRef(Date.now());
  const stripeSectionRef = useRef(null);
  const sid = useMemo(getSessionId, []);

  // Scroll to the Stripe checkout section the moment it mounts. Uses
  // requestAnimationFrame(x2) so the layout has settled after the state
  // flip before we scroll — otherwise the element's rect is still 0.
  useEffect(() => {
    if (!showStripe) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        stripeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }, [showStripe]);

  // ------------------------------------------------------------------
  // 1. Load listing + comps + activity
  // ------------------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const l = await fetchListing(slug);
        setListing(l);
        setForm((f) => ({ ...f, amount: l.price?.toString() || "" }));
        // Default the purchase-card option to `offer` on offer-only listings
        // — otherwise the `buy` option is filtered out but stays selected,
        // and `primaryLabel()` falls through to "Enquire about renting".
        if (!(Number(l.price || 0) > 0)) {
          setSelectedOption("offer");
        }
        fetchActivity(slug).then(setActivity).catch(() => {});
        fetchLinkedProjects(l.slug || slug)
          .then((r) => setLinkedProjects(r?.items || []))
          .catch(() => setLinkedProjects([]));
        const tld = (l.slug || l.domain || "").split(".").slice(-1)[0];
        if (tld) fetchComps(tld, 6).then((r) => setComps(r?.items || [])).catch(() => {});
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  // 2. Side-effect only: ensure Swetrix analytics is attached when the
  //    listing carries a project ID. SEO (title/meta/OG/JSON-LD) is
  //    handled declaratively by the <Seo> component below in the JSX.
  useEffect(() => {
    if (!listing) return;
    if (listing.swetrix_project_id) ensureSwetrix(listing.swetrix_project_id);
  }, [listing]);

  // 3. Analytics + payment return handling
  useEffect(() => {
    if (!listing) return;
    const params = new URLSearchParams(window.location.search);
    const returnedSessionId = params.get("session_id");
    const embeddedReturn = params.get("embedded") === "1";
    const bunqReturn = params.get("bunq") === "1";
    const bunqTabId = params.get("bunq_tab_id");
    // Payment Element return: Stripe appends payment_intent + client_secret
    // on wallet-redirect flows; we ALSO add ?pi=<id> ourselves for the
    // card path (no redirect happens). Either signal triggers polling.
    const returnedIntentId = params.get("pi") || params.get("payment_intent");
    if (returnedSessionId || (bunqReturn && bunqTabId) || returnedIntentId) {
      setPayStatus("checking");
      const poll = async (attempt = 0) => {
        if (attempt >= 10) { setPayStatus("failed"); toast.error("Payment status timed out. Check your email."); return; }
        try {
          let r;
          if (bunqReturn && bunqTabId) r = await fetchBunqStatus(bunqTabId);
          else if (returnedIntentId) r = await fetchIntentStatus(returnedIntentId);
          else if (embeddedReturn && returnedSessionId) r = await fetchEmbeddedStatus(returnedSessionId);
          else if (returnedSessionId) r = await fetchPaymentStatus(returnedSessionId);
          if (r && r.payment_status === "paid") { setPayStatus("paid"); toast.success("Payment received. Thank you!"); return; }
          if (r && r.status === "expired") { setPayStatus("failed"); return; }
        } catch { /* keep polling */ }
        setTimeout(() => poll(attempt + 1), 2000);
      };
      poll();
    } else if (params.get("cancelled")) {
      toast.warning("Payment cancelled");
    }
    trackEvent(slug, {
      event: "view", session_id: sid, referrer: document.referrer || null,
      utm_source: params.get("utm_source"), utm_medium: params.get("utm_medium"), utm_campaign: params.get("utm_campaign"),
      path: window.location.pathname,
    });
    const onScroll = () => {
      const pct = Math.round(((window.scrollY + window.innerHeight) / document.body.scrollHeight) * 100);
      [25, 50, 75, 100].forEach((mark) => {
        if (pct >= mark && !scrollTracked.current.has(mark)) {
          scrollTracked.current.add(mark);
          trackEvent(slug, { event: `scroll_${mark}`, session_id: sid, scroll_pct: mark });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const onLeave = () => trackEvent(slug, { event: "leave", session_id: sid, duration_ms: Date.now() - startedAt.current });
    window.addEventListener("beforeunload", onLeave);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("beforeunload", onLeave); };
  }, [listing, slug, sid]);

  if (notFound) {
    return (
      <div className="cd-marketplace" style={{ minHeight: "100vh", display: "grid", placeItems: "center", fontFamily: "var(--cd-font)", background: "var(--cd-white)" }}>
        <div style={{ textAlign: "center" }}>
          <div className="cd-eyebrow">404</div>
          <div className="cd-h2" style={{ marginTop: 8 }}>This domain is not currently listed.</div>
          <Link to="/" className="cd-btn cd-btn--primary" style={{ marginTop: 20 }}>Back to marketplace</Link>
        </div>
      </div>
    );
  }
  if (!listing) {
    return <div style={{ padding: 60, textAlign: "center", fontFamily: "var(--cd-font)", color: "var(--cd-gray-500)" }}>Loading…</div>;
  }

  const sym = currencySymbol(listing.currency);
  const hasPrice = Number(listing.price || 0) > 0;
  const isSold = listing.status === "sold";
  const price = hasPrice ? `${sym}${Number(listing.price).toLocaleString()}` : "";
  const leaseMonthly = hasPrice ? Math.ceil(Number(listing.price) / 24) : null;
  const accent = listing.category_accent || "#3ee8d3";
  const netTotal = discount ? Math.max(Number(listing.price) - Number(discount.amount), 1) : null;
  const netTotalLabel = netTotal ? `${sym}${Number(netTotal).toLocaleString()}` : "";

  // ---------------- checkout handlers ----------------
  const openBuy = async () => {
    if (isSold) return;
    trackEvent(slug, { event: "click_buy", session_id: sid });
    swetrixTrack("click_buy", { domain: listing.domain, price: listing.price, currency: listing.currency });
    setBuying(true);
    try {
      const plan = checkoutPlan || (await fetchCheckoutPlan(slug));
      setCheckoutPlan(plan);
      if (plan.provider === "bunq") {
        const r = await createBunqPayment(slug, { origin_url: window.location.origin, buyer_email: form.email || null, discount_code: discount?.code || null });
        if (r.share_url) {
          const sep = r.share_url.includes("?") ? "&" : "?";
          window.location.href = `${r.share_url}${sep}bunq_tab_id=${r.tab_id}`;
        } else toast.error("Bunq did not return a payment URL");
      } else {
        setPublishableKey(plan.publishable_key || "");
        setShowStripe(true);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setBuying(false); }
  };

  const openOffer = (intent = "offer") => {
    setSelectedOption(intent);
    trackEvent(slug, { event: `click_${intent}`, session_id: sid });
    swetrixTrack(`click_${intent}`, { domain: listing.domain });
    setOfferOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.amount) { toast.error("Name, email and amount are required"); return; }
    setSubmitting(true);
    try {
      await submitOffer(slug, {
        name: form.name, email: form.email, amount: Number(form.amount),
        message: form.message, intent: selectedOption, session_id: sid,
      });
      trackEvent(slug, { event: `submit_${selectedOption}`, session_id: sid });
      swetrixTrack(`submit_${selectedOption}`, { domain: listing.domain, amount: form.amount });
      toast.success("Thanks. You'll hear back within a business day.");
      setTimeout(() => setOfferOpen(false), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  // ---------------- render ----------------
  const brandName = brand?.brand_name || "CasperDomains";
  const priceCopy = listing.price ? `priced at ${currencySymbol(listing.currency)}${Number(listing.price).toLocaleString()}` : "accepting offers";

  // ---- SEO helpers (memoized to avoid re-computing on every render) ----
  // The lander is the single most important SEO surface — it's what
  // Google indexes and what a serious buyer decides against. Everything
  // below feeds that: rich Product schema (brand, sku, url, image),
  // BreadcrumbList for the sitelinks, and a description that mentions
  // the category + AI tagline + price so long-tail queries match.
  const canonicalUrl = typeof window !== "undefined"
    ? `${window.location.origin}/l/${listing.domain}`
    : `https://app.casperdomains.com/l/${listing.domain}`;
  const aiCopy = listing.ai_copy;
  const aiTagline = typeof aiCopy === "string"
    ? aiCopy
    : (aiCopy?.tagline || aiCopy?.headline || aiCopy?.description || "");
  // Social crawlers reject SVG in og:image, so point them at the
  // server-rendered 1200x630 PNG card (domain + price on the navy hero).
  const ogImage = `${process.env.REACT_APP_BACKEND_URL || ""}/api/og/l/${listing.slug || listing.domain}.png`;
  const seoDescription = [
    aiTagline ? aiTagline.replace(/\s+/g, " ").trim() : `${listing.domain} — a premium ${listing.category ? listing.category + " " : ""}domain for sale.`,
    priceCopy.charAt(0).toUpperCase() + priceCopy.slice(1) + ".",
    `Instant checkout via Stripe. Escrow-secured. Transfers within 24 hours via ${brandName}.`,
  ].join(" ").slice(0, 300);

  // Return-flow takeover: when the buyer just came back from a Stripe
  // wallet redirect (or the card path finished inline and we swapped
  // the URL to `?pi=...`), render a dedicated confirmation screen
  // instead of the normal lander so the buyer isn't looking at a Buy
  // button while their bank finishes clearing the payment.
  if (returnFlow) {
    return (
      <div className="cd-marketplace" style={{ background: "var(--cd-white)", color: "var(--cd-black)", fontFamily: "var(--cd-font)", minHeight: "100vh" }} data-testid="lander-page">
        <Seo
          title={`Confirming payment · ${listing.domain} · ${brandName}`}
          description={`Confirming your payment for ${listing.domain}.`}
          type="website"
          siteName={brandName}
        />
        <SiteHeader />
        <PaymentConfirmScreen
          payStatus={payStatus}
          listing={listing}
          currencySymbol={currencySymbol}
          brandName={brandName}
          onRetry={() => {
            const url = new URL(window.location.href);
            ["payment_intent", "payment_intent_client_secret", "pi", "redirect_status", "session_id", "embedded"].forEach((k) => url.searchParams.delete(k));
            window.history.replaceState({}, "", url.toString());
            setReturnFlow(false);
            setPayStatus(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="cd-marketplace" style={{ background: "var(--cd-white)", color: "var(--cd-black)", fontFamily: "var(--cd-font)" }} data-testid="lander-page">
      <Seo
        title={`${listing.domain} · for sale · ${brandName}`}
        description={seoDescription}
        canonical={canonicalUrl}
        type="website"
        siteName={brandName}
        image={ogImage}
        twitterHandle="@casperdomains"
        structuredData={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Product",
              "@id": `${canonicalUrl}#product`,
              name: listing.domain,
              description: seoDescription,
              sku: listing.domain,
              productID: listing.domain,
              url: canonicalUrl,
              image: ogImage,
              category: listing.category || "Domain names",
              brand: { "@type": "Organization", name: brandName },
              offers: {
                "@type": "Offer",
                url: canonicalUrl,
                price: listing.price ? Number(listing.price) : undefined,
                priceCurrency: (listing.currency || "USD").toUpperCase(),
                availability: listing.status === "sold"
                  ? "https://schema.org/SoldOut"
                  : "https://schema.org/InStock",
                itemCondition: "https://schema.org/NewCondition",
                seller: { "@type": "Organization", name: brandName, url: canonicalUrl.split("/l/")[0] },
              },
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: canonicalUrl.split("/l/")[0] },
                listing.category ? {
                  "@type": "ListItem",
                  position: 2,
                  name: listing.category,
                  item: `${canonicalUrl.split("/l/")[0]}/category/${String(listing.category).toLowerCase().replace(/\s+/g, "-")}`,
                } : null,
                { "@type": "ListItem", position: listing.category ? 3 : 2, name: listing.domain, item: canonicalUrl },
              ].filter(Boolean),
            },
          ],
        }}
      />
      <SiteHeader />

      {payStatus === "paid" && (
        <div
          data-testid="payment-success-banner"
          style={{ background: "var(--cd-success-bg)", borderBottom: "1px solid #a7f3d0", color: "#065f46", textAlign: "center", padding: "12px 18px", fontSize: 13, fontWeight: 600 }}
        >
          Payment confirmed. Check your inbox for the receipt.
        </div>
      )}

      {/* =============== HERO (navy) + floating purchase card =============== */}
      <section className="cd-domain-hero" data-testid="lander-hero">
        <div className="cd-container cd-domain-hero__inner">
          <div className="cd-rise">
            <span
              className="cd-domain-tag"
              data-testid="lander-category-tag"
              style={{ color: accent, borderColor: `${accent}66`, background: `${accent}1a` }}
            >
              <Globe size={12} strokeWidth={2} />{" "}
              {listing.category ? `${listing.category} domain · for sale` : "Premium domain · for sale"}
            </span>
            <h1 className="cd-domain-name" data-testid="lander-domain" style={{ marginTop: 22 }}>{listing.domain}</h1>
            {(() => {
              // ai_copy may be a string OR an object {headline, tagline, description, ...}
              const a = listing.ai_copy;
              const text = typeof a === "string" ? a : (a?.tagline || a?.description || a?.headline || "");
              return text ? (
                <p style={{ marginTop: 20, maxWidth: 560, fontSize: 18, lineHeight: 1.6, color: "rgba(255,255,255,.78)" }}>
                  {text}
                </p>
              ) : null;
            })()}

            <ul className="cd-check-list" style={{ marginTop: 26 }}>
              <li style={{ color: "rgba(255,255,255,.78)" }}><Check size={14} /> Owned outright · pushed within 24 hours of payment</li>
              <li style={{ color: "rgba(255,255,255,.78)" }}><Check size={14} /> Escrow.com secured on deals over €5,000</li>
              <li style={{ color: "rgba(255,255,255,.78)" }}><Check size={14} /> Stripe · SEPA · iDEAL · Escrow.com accepted</li>
            </ul>

            {comps.length > 0 && (
              <div style={{ marginTop: 26, padding: "16px 20px", borderRadius: 14, border: "1px solid rgba(255,255,255,.14)", background: "rgba(255,255,255,.04)" }}>
                <div style={{ fontSize: 11, letterSpacing: ".12em", textTransform: "uppercase", color: "rgba(255,255,255,.5)", marginBottom: 8 }}>
                  Recent comparable sales · via Namebio
                </div>
                <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: 13, color: "rgba(255,255,255,.85)" }}>
                  {comps.slice(0, 4).map((c) => (
                    <span key={c.domain}><strong>{c.domain}</strong> · {c.currency === "USD" ? "$" : c.currency}{Number(c.amount).toLocaleString()}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <PurchaseCard
            listing={listing}
            sym={sym}
            price={price}
            hasPrice={hasPrice}
            isSold={isSold}
            leaseMonthly={leaseMonthly}
            selected={selectedOption}
            setSelected={setSelectedOption}
            openBuy={openBuy}
            openOffer={openOffer}
            buying={buying}
          />
          {listing.is_brand_starter && listing.brand_starter_includes?.length > 0 && (
            <BrandStarterCallout includes={listing.brand_starter_includes} price={price} />
          )}
        </div>
      </section>

      {/* Space for the floating purchase card negative-margin overhang */}
      <div style={{ height: 220 }} aria-hidden />

      {/* =============== Trust benefits =============== */}
      <section className="cd-container" aria-label="Trust">
        <div className="cd-trust-grid">
          <div className="cd-trust-item">
            <div className="cd-trust-item__icon"><ShieldCheck strokeWidth={1.5} /></div>
            <h3>Escrow-secured</h3>
            <p>Deals over €5,000 clear through Escrow.com, so you never wire us blind.</p>
          </div>
          <div className="cd-trust-item">
            <div className="cd-trust-item__icon"><Clock strokeWidth={1.5} /></div>
            <h3>Push in 24 hours</h3>
            <p>Payment confirmed before end-of-day usually pushes the same evening.</p>
          </div>
          <div className="cd-trust-item">
            <div className="cd-trust-item__icon"><KeyRound strokeWidth={1.5} /></div>
            <h3>Ownership in your name</h3>
            <p>Full transfer to your registrar, including the auth code and DNS control.</p>
          </div>
        </div>
      </section>

      {/* =============== Share row =============== */}
      <ShareRow
        slug={slug}
        domain={listing.domain}
        priceLabel={price}
        sessionId={sid}
        accent={accent}
        onCodeIssued={(c) => {
          setDiscount(c);
          toast.success(`${sym}${Number(c.amount).toLocaleString()} off applied — code ${c.code}`);
        }}
      />

      {/* =============== Featured in a project (reverse cross-link) =============== */}
      {linkedProjects.length > 0 && (
        <section
          className="cd-container"
          data-testid="lander-featured-in-projects"
          style={{ marginTop: 40 }}
          aria-label="Featured in projects"
        >
          <div
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 16,
              padding: "18px 22px",
              borderRadius: 16,
              border: "1px solid var(--cd-gray-200)",
              background: "linear-gradient(135deg, rgba(75,111,255,.05), rgba(62,232,211,.05))",
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 12px",
                borderRadius: 999,
                background: "var(--cd-navy-950)",
                color: "#fff",
                fontFamily: "var(--cd-font)",
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                alignSelf: "center",
              }}
            >
              Featured in
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              {linkedProjects.slice(0, 2).map((lp) => (
                <Link
                  key={lp.slug}
                  to={`/projects/${lp.slug}`}
                  data-testid={`lander-linked-project-${lp.slug}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "6px 0",
                    color: "var(--cd-navy-950)",
                    textDecoration: "none",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontFamily: "var(--cd-font)", fontWeight: 700, fontSize: 15 }}>
                      {lp.title}
                    </div>
                    {lp.summary && (
                      <div
                        style={{
                          fontSize: 13,
                          color: "var(--cd-gray-600)",
                          marginTop: 2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {lp.summary}
                      </div>
                    )}
                  </div>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      color: "var(--cd-blue-500)",
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Read write-up <ArrowUpRight size={13} strokeWidth={2.4} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* =============== Why this domain =============== */}
      <section className="cd-section cd-section--soft" aria-label="Why this domain">
        <div className="cd-container cd-split">
          <div>
            <span className="cd-eyebrow">Why {listing.domain}</span>
            <h2 className="cd-h2" style={{ marginTop: 8 }}>
              A name built for the way people search, spell, and remember.
            </h2>
            <p className="cd-lead" style={{ marginTop: 16 }}>
              {(() => {
                const lc = listing.long_copy;
                const s = typeof lc === "string" ? lc : (lc?.description || lc?.tagline || "");
                return s || `${listing.domain} is a compact, memorable domain in an active category. Short enough to speak in one breath, distinct enough to survive a decade of brand evolution.`;
              })()}
            </p>
            <div className="cd-feature-list">
              <div className="cd-feature">
                <Zap className="cd-feature__icon" strokeWidth={1.5} />
                <div><strong>Short &amp; memorable</strong><p>Fits every screen and every business card.</p></div>
              </div>
              <div className="cd-feature">
                <Globe className="cd-feature__icon" strokeWidth={1.5} />
                <div><strong>International-ready</strong><p>Neutral spelling, easy to pronounce across languages.</p></div>
              </div>
              <div className="cd-feature">
                <ShieldCheck className="cd-feature__icon" strokeWidth={1.5} />
                <div><strong>Clean history</strong><p>No trademark friction, no penalized backlinks.</p></div>
              </div>
            </div>
          </div>
          <div className="cd-split__visual">
            <div className="cd-illus" aria-hidden>
              {listing.domain_logo_url ? (
                <div className="cd-domain-logo-card" data-testid="lander-domain-logo">
                  <div className="cd-domain-logo-card__accent" />
                  <div className="cd-domain-logo-card__inner">
                    <img
                      src={listing.domain_logo_url}
                      alt={`${listing.domain} logo`}
                      className="cd-domain-logo-card__img"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                    <div className="cd-domain-logo-card__label">{listing.domain}</div>
                  </div>
                </div>
              ) : (
                <svg viewBox="0 0 480 360" xmlns="http://www.w3.org/2000/svg">
                  <rect x="60" y="80" width="360" height="200" rx="24" fill="#fff" stroke="#eaecf0" strokeWidth="2" />
                  <text x="240" y="200" textAnchor="middle" fontFamily="Inter" fontWeight="800" fontSize="42" fill="#03142f" letterSpacing="-2">
                    {listing.domain.length > 22 ? listing.domain.slice(0, 20) + "…" : listing.domain}
                  </text>
                  <rect x="60" y="80" width="360" height="10" fill="url(#g1)" />
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0" stopColor="#4b6fff" /><stop offset="1" stopColor="#3ee8d3" />
                    </linearGradient>
                  </defs>
                </svg>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Inline Stripe checkout (lazy-mounted after Buy click) */}
      {showStripe && checkoutPlan?.provider === "stripe" && (
        <section
          className="cd-checkout"
          data-testid="lander-stripe-section"
          ref={stripeSectionRef}
          id="stripe-checkout-section"
        >
          <div className="cd-container">
            <div className="cd-checkout-progress" aria-label="Checkout progress">
              <div className="cd-progress-step is-done">Domain</div>
              <div className="cd-progress-step is-active">Payment</div>
              <div className="cd-progress-step">Transfer</div>
            </div>
            <div className="cd-checkout__grid">
              <div className="cd-payment-card">
                <h3 className="cd-h3" style={{ marginBottom: 6 }}>Payment</h3>
                <p className="cd-muted" style={{ fontSize: 14, marginBottom: 18 }}>Secured by Stripe. Cards, Apple Pay &amp; Link supported.</p>
                <StripePaymentElement
                  key={discount?.code || "no-code"}
                  slug={slug}
                  buyerEmail={form.email || null}
                  discountCode={discount?.code || null}
                  onDiscountRejected={() => {
                    setDiscount(null);
                    toast.error("That discount code is no longer valid — the total has been updated.");
                  }}
                  onClose={() => setShowStripe(false)}
                />
              </div>
              <aside className="cd-order-summary" data-testid="order-summary">
                <h3 className="cd-h3" style={{ marginBottom: 6 }}>Order summary</h3>
                <p className="cd-muted" style={{ fontSize: 13, marginBottom: 16 }}>One-time purchase · lifetime ownership.</p>
                <div className="cd-summary-row"><span>{listing.domain}</span><span>{price}</span></div>
                <div className="cd-summary-row"><span>Transfer fee</span><span>Included</span></div>
                <div className="cd-summary-row cd-summary-row--discount"><span>Escrow fee</span><span>We cover it</span></div>
                {discount && (
                  <div className="cd-summary-row cd-summary-row--discount" data-testid="order-discount-row">
                    <span>Share discount · {discount.code}</span>
                    <span>−{sym}{Number(discount.amount).toLocaleString()}</span>
                  </div>
                )}
                <div className="cd-summary-total">
                  <span>Total today</span>
                  <span data-testid="order-total">{netTotalLabel || price}</span>
                </div>
                <DiscountField
                  slug={slug}
                  discount={discount}
                  onApplied={setDiscount}
                  onCleared={() => setDiscount(null)}
                />
                <ul className="cd-check-list" style={{ marginTop: 18 }}>
                  <li><Check size={14} /> Ownership pushed to your registrar</li>
                  <li><Check size={14} /> Broker reachable via hello@casperdomains.com</li>
                </ul>
                <PaymentMethods className="cd-pay-strip" />
              </aside>
            </div>
          </div>
        </section>
      )}

      <RelatedBrandsRail category={listing?.category} exclude={listing?.slug} />
      <SiteFooter />
      <CompareTray />
      <SnitcherEmbed />
      <BullseyeEmbed page="lander" />

      {/* Offer modal */}
      {offerOpen && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="offer-modal"
          style={{
            position: "fixed", inset: 0, background: "rgba(3,20,47,0.55)", backdropFilter: "blur(10px)",
            display: "grid", placeItems: "center", zIndex: 100, padding: 20,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setOfferOpen(false); }}
        >
          <form
            onSubmit={submit}
            className="cd-payment-card"
            style={{ maxWidth: 520, width: "100%" }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
              <div>
                <h3 className="cd-h3">
                  {selectedOption === "offer" ? "Make an offer" : selectedOption === "lease" ? "Lease to own" : "Rent this domain"}
                </h3>
                <p className="cd-muted" style={{ fontSize: 13, marginTop: 4 }}>
                  {listing.domain} · reply within one business day
                </p>
              </div>
              <button type="button" onClick={() => setOfferOpen(false)} className="cd-btn cd-btn--ghost cd-btn--sm" data-testid="offer-close" aria-label="Close">
                <X size={16} />
              </button>
            </div>

            <div style={{ marginTop: 16 }}>
              <label style={fieldLabel}>Your name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cd-input" placeholder="Jane Doe" data-testid="offer-name" />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={fieldLabel}>Email</label>
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" required className="cd-input" placeholder="you@company.com" data-testid="offer-email" />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={fieldLabel}>{selectedOption === "rent" ? "Monthly rent" : "Your offer"} ({sym})</label>
              <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} type="number" required className="cd-input" placeholder={hasPrice ? `${Math.round(listing.price * 0.7)}` : "5000"} data-testid="offer-amount" />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={fieldLabel}>Message (optional)</label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="cd-input" style={{ height: 100, padding: 12 }} placeholder="Anything you'd like me to know…" data-testid="offer-message" />
            </div>
            <button className="cd-btn cd-btn--primary cd-btn--full" style={{ marginTop: 20 }} disabled={submitting} data-testid="offer-submit">
              {submitting ? "Sending…" : "Send"} <ArrowRight size={16} />
            </button>
          </form>
        </div>
      )}

      {/* Stripe modal fallback (unused when inline is active) */}
    </div>
  );
}

/* --------------------------- purchase card --------------------------- */

const fieldLabel = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: ".08em",
  color: "var(--cd-gray-700)",
  marginBottom: 6,
};

function PurchaseCard({
  listing, sym, price, hasPrice, isSold, leaseMonthly,
  selected, setSelected, openBuy, openOffer, buying,
}) {
  const options = [
    { key: "buy", icon: <CreditCard size={16} />, title: "Buy now", sub: "One-time purchase, full ownership", price, show: hasPrice && !isSold },
    { key: "offer", icon: <Handshake size={16} />, title: "Make an offer", sub: "Reply within a business day", price: "Free" },
    { key: "lease", icon: <Repeat size={16} />, title: "Lease to own", sub: leaseMonthly ? `From ${sym}${leaseMonthly.toLocaleString()}/mo · 24 months` : "Monthly instalments", price: leaseMonthly ? `${sym}${leaseMonthly.toLocaleString()}/mo` : "" },
    { key: "rent", icon: <KeyRound size={16} />, title: "Rent", sub: "Short-term use, upgrade any time", price: "Contact" },
  ];
  const primary = () => {
    if (isSold) return null;
    if (selected === "buy" && hasPrice) return openBuy();
    return openOffer(selected);
  };
  const primaryLabel = () => {
    if (isSold) return "Sold";
    if (selected === "buy" && hasPrice) return buying ? "Loading…" : `Buy for ${price}`;
    if (selected === "offer") return "Make an offer";
    if (selected === "lease") return "Start lease-to-own";
    return "Enquire about renting";
  };

  return (
    <aside className="cd-purchase-card cd-card" data-testid="purchase-card">
      <h3 className="cd-purchase-card__title">
        {hasPrice ? price : "Make an offer"}
      </h3>
      <p className="cd-purchase-card__sub">
        {isSold ? "This domain has sold." : hasPrice ? "Listed price · negotiable" : "No fixed price · every serious offer considered"}
      </p>

      <div>
        {options.filter((o) => o.show !== false).map((o) => (
          <button
            key={o.key}
            type="button"
            className={`cd-buy-option ${selected === o.key ? "is-active" : ""}`}
            onClick={() => setSelected(o.key)}
            data-testid={`buy-option-${o.key}`}
            style={{ background: "transparent", textAlign: "left", border: 0, width: "100%", cursor: isSold && o.key === "buy" ? "not-allowed" : "pointer" }}
            disabled={isSold && o.key === "buy"}
          >
            <span aria-hidden style={{ display: "grid", placeItems: "center", width: 24, height: 24, borderRadius: 6, background: selected === o.key ? "var(--cd-cyan-400)" : "var(--cd-gray-100)", color: selected === o.key ? "var(--cd-navy-950)" : "var(--cd-gray-700)" }}>
              {o.icon}
            </span>
            <span>
              <strong>{o.title}</strong>
              <small>{o.sub}</small>
            </span>
            <span className="cd-buy-option__price">{o.price}</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className="cd-btn cd-btn--primary cd-btn--full"
        style={{ marginTop: 20 }}
        onClick={primary}
        disabled={isSold || buying}
        data-testid="purchase-card-cta"
      >
        {primaryLabel()} <ArrowRight size={16} />
      </button>

      <div style={{ marginTop: 12 }}>
        {listing.spaceship_buy_url ? (
          <a
            href={listing.spaceship_buy_url}
            target="_blank"
            rel="noopener noreferrer"
            className="cd-btn cd-btn--outline cd-btn--full"
            data-testid="purchase-card-spaceship"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              width: "100%",
              padding: "12px 16px",
              borderRadius: 12,
              border: "1px solid var(--cd-gray-200)",
              background: "var(--cd-white)",
              color: "var(--cd-navy-950)",
              fontFamily: "var(--cd-font)",
              fontWeight: 700,
              fontSize: 12,
              letterSpacing: ".04em",
              textTransform: "uppercase",
              textDecoration: "none",
              transition: "background .18s ease, color .18s ease, border-color .18s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f6f7f9";
              e.currentTarget.style.borderColor = "var(--cd-navy-950)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "var(--cd-white)";
              e.currentTarget.style.borderColor = "var(--cd-gray-200)";
            }}
          >
            <span style={{ color: "var(--cd-gray-500)", fontSize: 11 }}>Buy on</span>
            <span
              aria-label="Spaceship"
              role="img"
              style={{
                display: "inline-block",
                width: 96,
                height: 22,
                backgroundImage: 'url("https://spaceship-cdn.com/cms/Versions_01.01k1ttqd8h3z7vzgs7m7yqg9k0.svg")',
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
                backgroundSize: "150px auto",
              }}
            />
            <ArrowUpRight size={14} strokeWidth={2.4} color="var(--cd-gray-500)" />
          </a>
        ) : null}
      </div>

      <ul className="cd-check-list" data-testid="purchase-perks">
        <li><Check size={14} /> Escrow-secured on deals over €5,000</li>
        <li><Check size={14} /> Push within 24h of confirmed payment</li>
      </ul>

      {hasPrice && !isSold && (
        <QRHandoff
          url={listing.spaceship_buy_url || (typeof window !== "undefined" ? window.location.href : "")}
          label={listing.spaceship_buy_url ? "Buy on your phone" : "Continue on your phone"}
          sublabel={listing.spaceship_buy_url
            ? "Scan to open the Spaceship checkout on mobile. No email needed."
            : "Scan with your camera to open this listing on your phone."}
        />
      )}
    </aside>
  );
}


/**
 * PaymentConfirmScreen — full-page takeover shown when the buyer returns
 * from a Stripe wallet redirect (iDEAL / Bancontact / EPS / etc.) OR
 * when the card path finished inline. Three states, one component:
 *
 *   - `checking` (or null while polling starts): centered spinner + copy
 *     explaining the payment is being confirmed with the bank.
 *   - `paid`: green success card with the amount, the domain, and the
 *     immediate next step ("we've emailed you a receipt, transfer in
 *     under 24h").
 *   - `failed`: warning card with a "Try again" button that clears the
 *     URL and drops the buyer back to the normal lander.
 *
 * Rendered by the Lander only. Uses the same visual language as the
 * navy hero so the transition from checkout → confirmation feels
 * continuous rather than jarring.
 */
function PaymentConfirmScreen({ payStatus, listing, currencySymbol, brandName, onRetry }) {
  const state = payStatus === "paid" ? "paid" : payStatus === "failed" ? "failed" : "checking";
  const sym = currencySymbol(listing.currency);
  const priceLabel = listing.price ? `${sym}${Number(listing.price).toLocaleString()}` : "";

  // Live-status ticker for the checking state. Advances through
  // reassuring stages so the 20–30s wait feels active. Stops the
  // moment we know the payment resolved either way.
  const CHECK_STAGES = [
    "Contacted your bank",
    "Waiting for confirmation",
    "Almost done",
  ];
  const [checkStage, setCheckStage] = useState(0);
  useEffect(() => {
    if (state !== "checking") return;
    setCheckStage(0);
    const t = setInterval(() => {
      // Cap at last stage — the copy shouldn't jump back to stage 0
      // even if polling somehow takes longer than expected.
      setCheckStage((s) => (s < CHECK_STAGES.length - 1 ? s + 1 : s));
    }, 6000);
    return () => clearInterval(t);
  }, [state]);

  return (
    <section
      className="cd-domain-hero"
      data-testid={`payment-confirm-${state}`}
      style={{ minHeight: "calc(100vh - 82px)", display: "grid", placeItems: "center", padding: "60px 0" }}
    >
      <div
        className="cd-container"
        style={{ position: "relative", zIndex: 2, maxWidth: 620, textAlign: "center" }}
      >
        {state === "checking" && (
          <>
            <StatusPill stages={CHECK_STAGES} activeIndex={checkStage} />
            <div style={{ marginTop: 26 }}>
              <ConfirmSpinner />
            </div>
            <h1
              className="cd-domain-name"
              style={{ marginTop: 32, fontSize: "clamp(32px, 4vw, 48px)", color: "#fff" }}
            >
              Confirming your payment…
            </h1>
            <p style={{ marginTop: 18, color: "rgba(255,255,255,.78)", fontSize: 17, lineHeight: 1.6 }}>
              Your bank is finalising the {priceLabel && <>{priceLabel} </>}payment for <strong style={{ color: "#fff" }}>{listing.domain}</strong>.
              This usually takes 10–30 seconds. Please don't close this tab.
            </p>
            <div
              style={{
                marginTop: 40,
                padding: "18px 22px",
                background: "rgba(255,255,255,.06)",
                border: "1px solid rgba(255,255,255,.14)",
                borderRadius: 14,
                color: "rgba(255,255,255,.72)",
                fontSize: 13,
                textAlign: "left",
              }}
            >
              <strong style={{ color: "var(--cd-cyan-400)", display: "block", marginBottom: 6, letterSpacing: ".08em", fontSize: 11, textTransform: "uppercase" }}>
                What happens next
              </strong>
              As soon as we get the confirmation we'll email you a receipt and start the transfer to your registrar. If anything fails you'll see it right here.
            </div>
          </>
        )}

        {state === "paid" && (
          <>
            <SuccessCheckmark />
            <span
              className="cd-domain-tag"
              style={{
                marginTop: 24,
                background: "rgba(18,183,106,.16)",
                color: "#22c55e",
                display: "inline-flex",
              }}
            >
              Payment received
            </span>
            <h1
              className="cd-domain-name"
              style={{ marginTop: 18, fontSize: "clamp(32px, 4vw, 48px)", color: "#fff" }}
            >
              Thanks for buying {listing.domain}.
            </h1>
            <p style={{ marginTop: 18, color: "rgba(255,255,255,.78)", fontSize: 17, lineHeight: 1.6 }}>
              We've emailed you a receipt {priceLabel && <>for <strong style={{ color: "#fff" }}>{priceLabel}</strong> </>}
              and started the transfer to your registrar. You'll have the auth code within one business day, usually sooner.
            </p>
            <div style={{ marginTop: 36, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <Link
                to="/"
                className="cd-btn cd-btn--primary"
                data-testid="confirm-back-to-marketplace"
                style={{ background: "#fff", color: "var(--cd-navy-950)", padding: "12px 22px", borderRadius: 12 }}
              >
                Back to marketplace
              </Link>
              <a
                href="mailto:hello@casperdomains.com"
                className="cd-btn cd-btn--outline"
                data-testid="confirm-email-broker"
                style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,.4)", padding: "12px 22px", borderRadius: 12 }}
              >
                Email the broker
              </a>
            </div>
          </>
        )}

        {state === "failed" && (
          <>
            <FailureCross />
            <span
              className="cd-domain-tag"
              style={{
                marginTop: 24,
                background: "rgba(180,35,24,.18)",
                color: "#fca5a5",
                display: "inline-flex",
              }}
            >
              Payment didn't clear
            </span>
            <h1
              className="cd-domain-name"
              style={{ marginTop: 18, fontSize: "clamp(30px, 4vw, 44px)", color: "#fff" }}
            >
              Your bank couldn't finish the payment.
            </h1>
            <p style={{ marginTop: 18, color: "rgba(255,255,255,.78)", fontSize: 17, lineHeight: 1.6 }}>
              We didn't charge you. Try a different card or payment method, and if it still fails the broker is one email away.
            </p>
            <div style={{ marginTop: 36, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={onRetry}
                data-testid="confirm-retry"
                className="cd-btn cd-btn--primary"
                style={{ background: "#fff", color: "var(--cd-navy-950)", padding: "12px 22px", borderRadius: 12, border: 0, cursor: "pointer", fontWeight: 700 }}
              >
                Try again
              </button>
              <a
                href="mailto:hello@casperdomains.com"
                data-testid="confirm-help"
                className="cd-btn cd-btn--outline"
                style={{ background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,.4)", padding: "12px 22px", borderRadius: 12 }}
              >
                Get help
              </a>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/** Discount-code field in the order summary. Validates server-side so the
 *  displayed total always matches what Stripe will charge. */
function DiscountField({ slug, discount, onApplied, onCleared }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (discount) {
    return (
      <div
        data-testid="discount-applied"
        style={{ marginTop: 14, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}
      >
        <Check size={14} strokeWidth={2.4} style={{ color: "#047857" }} />
        <span style={{ color: "var(--cd-gray-700)" }}>
          Code <strong style={{ fontFamily: "monospace" }}>{discount.code}</strong> applied
        </span>
        <button
          type="button"
          onClick={() => { onCleared(); setValue(""); }}
          data-testid="discount-remove-btn"
          style={{ marginLeft: "auto", background: "none", border: 0, color: "#98A2B3", fontSize: 12, cursor: "pointer" }}
        >
          Remove
        </button>
      </div>
    );
  }

  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await validateDiscount(value.trim(), slug);
      if (r.valid) onApplied(r);
      else setError(r.reason || "Invalid code");
    } catch {
      setError("Could not check that code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={apply} data-testid="discount-form" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Discount code"
          data-testid="discount-input"
          style={{
            flex: 1, padding: "10px 12px", borderRadius: 10,
            border: "1px solid #E4E7EC", fontSize: 13, fontFamily: "inherit", outline: "none",
          }}
        />
        <button
          type="submit"
          disabled={busy || !value.trim()}
          data-testid="discount-apply-btn"
          style={{
            padding: "10px 16px", borderRadius: 10, border: "1px solid var(--cd-navy-950)",
            background: "white", color: "var(--cd-navy-950)", fontSize: 13,
            fontWeight: 700, cursor: busy ? "wait" : "pointer",
          }}
        >
          {busy ? "…" : "Apply"}
        </button>
      </div>
      {error && (
        <div data-testid="discount-error" style={{ marginTop: 8, fontSize: 12, color: "#B42318" }}>
          {error}
        </div>
      )}
    </form>
  );
}

/** Circular spinner with the CasperDomains cyan accent. */
function ConfirmSpinner() {
  return (
    <>
      <style>{`
        @keyframes cd-spin { to { transform: rotate(360deg); } }
        @keyframes cd-pill-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: .55; }
        }
        .cd-spinner { display:inline-block; width:52px; height:52px; border-radius:50%; border:3px solid rgba(255,255,255,.16); border-top-color: var(--cd-cyan-400); animation: cd-spin 0.9s linear infinite; }
        .cd-status-pill { display:inline-flex; align-items:center; gap:10px; padding:8px 18px 8px 12px; border-radius:999px; background: rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.16); color:#fff; font-size:13px; font-weight:600; letter-spacing:-0.01em; }
        .cd-status-pill__dot { width:8px; height:8px; border-radius:50%; background: var(--cd-cyan-400); animation: cd-pill-pulse 1.4s ease-in-out infinite; }
        .cd-status-pill__label { transition: opacity .35s ease; }
        .cd-status-pill__ticks { display:flex; gap:5px; margin-left:6px; }
        .cd-status-pill__tick { width:14px; height:2px; border-radius:1px; background: rgba(255,255,255,.18); transition: background .25s ease; }
        .cd-status-pill__tick.is-done { background: var(--cd-cyan-400); }
      `}</style>
      <span className="cd-spinner" aria-label="Confirming" role="status" data-testid="confirm-spinner" />
    </>
  );
}

/** Live-status pill that ticks through the payment-confirmation stages
 *  in the checking state. Pure presentational — the parent owns the
 *  active index so we don't split time-based state across components. */
function StatusPill({ stages, activeIndex }) {
  const label = stages[Math.min(activeIndex, stages.length - 1)];
  return (
    <div
      className="cd-status-pill"
      role="status"
      aria-live="polite"
      data-testid="confirm-status-pill"
    >
      <span className="cd-status-pill__dot" aria-hidden />
      <span className="cd-status-pill__label" key={activeIndex}>{label}</span>
      <span className="cd-status-pill__ticks" aria-hidden>
        {stages.map((_, i) => (
          <span
            key={i}
            className={`cd-status-pill__tick ${i <= activeIndex ? "is-done" : ""}`}
          />
        ))}
      </span>
    </div>
  );
}

function SuccessCheckmark() {
  return (
    <div
      aria-hidden
      style={{
        width: 68, height: 68, borderRadius: "50%",
        background: "rgba(18,183,106,.18)",
        border: "2px solid rgba(34,197,94,.5)",
        display: "grid", placeItems: "center", margin: "0 auto",
      }}
    >
      <Check size={30} strokeWidth={2.6} color="#22c55e" />
    </div>
  );
}

function FailureCross() {
  return (
    <div
      aria-hidden
      style={{
        width: 68, height: 68, borderRadius: "50%",
        background: "rgba(180,35,24,.18)",
        border: "2px solid rgba(252,165,165,.4)",
        display: "grid", placeItems: "center", margin: "0 auto",
        color: "#fca5a5", fontSize: 30, fontWeight: 800,
      }}
    >
      ×
    </div>
  );
}


/**
 * "Also from CasperDomains" rail — 3 brand-ready domains in the same
 * category as the current lander. Hidden entirely when no category is
 * set or no siblings exist, so unbranded listings stay clean.
 */
function RelatedBrandsRail({ category, exclude }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!category) return;
    api.get("/listings/brands-ready", { params: { limit: 12 } })
      .then((r) => {
        const filtered = (r.data?.items || [])
          .filter((d) => d.category === category && d.slug !== exclude)
          .slice(0, 3);
        setItems(filtered);
      })
      .catch(() => setItems([]));
  }, [category, exclude]);

  if (items.length === 0) return null;

  const backend = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <section
      className="cd-container"
      style={{ padding: "60px 0 40px" }}
      aria-label="Also from CasperDomains"
      data-testid="related-brands-rail"
    >
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", color: "var(--cd-gray-500)", textTransform: "uppercase" }}>
          Also from CasperDomains
        </div>
        <h3 style={{ fontFamily: "var(--cd-font-display)", fontSize: 26, marginTop: 8, color: "var(--cd-navy-950)" }}>
          More {category} brands, ready to launch.
        </h3>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 16,
        }}
      >
        {items.map((d) => {
          const logoSrc = d.domain_logo_url
            ? (d.domain_logo_url.startsWith("/api/") ? `${backend}${d.domain_logo_url}` : d.domain_logo_url)
            : "";
          return (
            <Link
              key={d.slug}
              to={`/l/${d.domain}`}
              data-testid={`related-brand-${d.domain}`}
              style={{
                display: "block",
                border: "1px solid var(--cd-gray-200)", borderRadius: 12,
                textDecoration: "none", color: "inherit",
                overflow: "hidden", background: "white",
                transition: "transform 200ms ease, border-color 200ms ease",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "var(--cd-navy-950)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.borderColor = "var(--cd-gray-200)"; }}
            >
              <div
                style={{
                  height: 100,
                  background: logoSrc
                    ? `#fafafa url("${logoSrc}") center / contain no-repeat`
                    : "linear-gradient(135deg, var(--cd-navy-950) 0%, #0b274d 100%)",
                  borderBottom: "1px solid var(--cd-gray-200)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {!logoSrc && (
                  <div style={{ color: "white", fontFamily: "var(--cd-font-display)", fontSize: 18, fontWeight: 600, padding: "0 16px", textAlign: "center", wordBreak: "break-word" }}>
                    {d.domain}
                  </div>
                )}
              </div>
              <div style={{ padding: "14px 16px" }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: "var(--cd-navy-950)", marginBottom: 4, wordBreak: "break-word" }}>
                  {d.domain}
                </div>
                <div style={{ fontSize: 14, color: "var(--cd-navy-950)", fontWeight: 600 }}>
                  {(d.currency || "EUR") === "EUR" ? "€" : "$"}{(d.price || 0).toLocaleString()}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}



/**
 * "Brand starter bundle" callout — rendered directly below the Purchase
 * card when the admin has flagged this listing as a Starter package.
 * Reinforces value: same price, more delivered.
 */
function BrandStarterCallout({ includes, price }) {
  return (
    <div
      style={{
        marginTop: 24,
        padding: "24px 24px 22px",
        border: "1px solid #93c5fd",
        borderRadius: 14,
        background: "linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)",
      }}
      data-testid="brand-starter-callout"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".1em", color: "#1e40af", background: "#dbeafe", padding: "3px 8px", borderRadius: 999, textTransform: "uppercase" }}>
          ★ Brand Starter Bundle
        </span>
        {price && (
          <span style={{ fontSize: 12, color: "var(--cd-gray-600)" }}>
            All-in for <strong style={{ color: "var(--cd-navy-950)" }}>{price}</strong>
          </span>
        )}
      </div>
      <div style={{ fontSize: 15, color: "var(--cd-navy-950)", fontWeight: 600, marginBottom: 14 }}>
        Everything you need to launch tomorrow.
      </div>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        {includes.map((line, i) => (
          <li
            key={i}
            style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 14, color: "var(--cd-gray-700)" }}
            data-testid={`starter-include-${i}`}
          >
            <span style={{ color: "#2563eb", fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
            <span>{line}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

